import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Brain, Shield, Database, Award, Mail, ChevronRight, Globe, Lock, FileCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LandingPage = () => {
  const [content, setContent] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    organization: '',
    jurisdiction: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`);
      setContent(data);
    } catch (error) {
      console.error('Failed to fetch content:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Thank you! We\'ll be in touch soon.');
      setFormData({ name: '', organization: '', jurisdiction: '', message: '' });
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!content) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10B981]"></div>
      </div>
    );
  }

  const hero = content.sections.find(s => s.section_id === 'hero')?.content || {};
  const expertise = content.sections.find(s => s.section_id === 'expertise')?.content || {};
  const services = content.sections.find(s => s.section_id === 'services')?.content || {};
  const security = content.sections.find(s => s.section_id === 'security')?.content || {};
  const about = content.sections.find(s => s.section_id === 'about')?.content || {};
  const contact = content.sections.find(s => s.section_id === 'contact')?.content || {};

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1743058067114-fc6675aa009c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBBc2lhbiUyMGFyY2hpdGVjdHVyZSUyMGdlb21ldHJpYyUyMHRlY2hub2xvZ3l8ZW58MHx8fHwxNzc4NjMwNTAxfDA&ixlib=rb-4.1.0&q=85"
            alt="Modern architecture"
            className="w-full h-full object-cover opacity-10"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl"
          >
            <h1 className="text-5xl sm:text-6xl tracking-tighter leading-none font-black text-[#0A0A0A] mb-8" data-testid="hero-headline">
              {hero.headline}
            </h1>
            <p className="text-base leading-relaxed font-normal text-[#52525B] mb-10 max-w-3xl" data-testid="hero-subheadline">
              {hero.subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#10B981] hover:bg-[#059669] text-white rounded-md px-8 py-6 text-base font-semibold transition-all hover:-translate-y-0.5 shadow-lg"
                data-testid="hero-cta-button"
              >
                <a href="#contact">{hero.cta_text} <ChevronRight className="ml-2 w-5 h-5" /></a>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-[#E4E4E7] hover:border-[#10B981] rounded-md px-8 py-6 text-base font-semibold transition-all"
                data-testid="hero-email-button"
              >
                <a href={`mailto:${hero.cta_email}`}><Mail className="mr-2 w-5 h-5" />Email Us</a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Expertise Section */}
      <section id="expertise" className="py-24 bg-[#F4F5F7]" data-testid="expertise-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#10B981] mb-4" data-testid="expertise-overline">{expertise.title}</div>
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-6" data-testid="expertise-title">{expertise.subtitle}</h2>
            <p className="text-base leading-relaxed font-normal text-[#52525B] mb-12 max-w-4xl" data-testid="expertise-description">{expertise.description}</p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {expertise.features?.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  className="bg-white p-8 rounded-md border border-[#E4E4E7] hover:-translate-y-1 hover:shadow-lg transition-all"
                  data-testid={`expertise-feature-${idx}`}
                >
                  <div className="w-12 h-12 rounded-md bg-[#10B981] flex items-center justify-center mb-4">
                    {idx === 0 ? <Globe className="w-6 h-6 text-white" /> : <Brain className="w-6 h-6 text-white" />}
                  </div>
                  <h3 className="text-2xl sm:text-3xl tracking-tight leading-snug font-semibold text-[#0A0A0A] mb-3">{feature.title}</h3>
                  <p className="text-base leading-relaxed font-normal text-[#52525B]">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-16 text-center" data-testid="services-title">{services.title}</h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              {services.services?.map((service, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.15, duration: 0.6 }}
                  className="bg-white p-8 rounded-md border border-[#E4E4E7] hover:-translate-y-1 hover:shadow-lg transition-all"
                  data-testid={`service-card-${idx}`}
                >
                  <div className="w-14 h-14 rounded-md bg-[#10B981] flex items-center justify-center mb-6">
                    {idx === 0 && <Brain className="w-7 h-7 text-white" />}
                    {idx === 1 && <Database className="w-7 h-7 text-white" />}
                    {idx === 2 && <Award className="w-7 h-7 text-white" />}
                  </div>
                  <h3 className="text-2xl sm:text-3xl tracking-tight leading-snug font-semibold text-[#0A0A0A] mb-4">{service.title}</h3>
                  <p className="text-base leading-relaxed font-normal text-[#52525B]">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-24 bg-[#F4F5F7]" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-4" data-testid="security-title">{security.title}</h2>
            <h3 className="text-2xl sm:text-3xl tracking-tight leading-snug font-semibold text-[#10B981] mb-6" data-testid="security-subtitle">{security.subtitle}</h3>
            <p className="text-base leading-relaxed font-normal text-[#52525B] mb-12 max-w-4xl" data-testid="security-description">{security.description}</p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {security.features?.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1, duration: 0.5 }}
                  className="bg-white p-8 rounded-md border border-[#E4E4E7]"
                  data-testid={`security-feature-${idx}`}
                >
                  <div className="w-12 h-12 rounded-md bg-[#0A0A0A] flex items-center justify-center mb-4">
                    {idx === 0 && <Lock className="w-6 h-6 text-white" />}
                    {idx === 1 && <Shield className="w-6 h-6 text-white" />}
                    {idx === 2 && <FileCheck className="w-6 h-6 text-white" />}
                  </div>
                  <h4 className="text-xl font-semibold text-[#0A0A0A] mb-3">{feature.title}</h4>
                  <p className="text-base leading-relaxed font-normal text-[#52525B]">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_xceltrain-landing/artifacts/kmmz5l6p_val-vesa-S_TDqSMifW8-unsplash.jpg"
                alt="Xceltrain team"
                className="rounded-md w-full shadow-2xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-6" data-testid="about-title">{about.title}</h2>
              <p className="text-base leading-relaxed font-normal text-[#52525B] mb-6" data-testid="about-description">{about.description}</p>
              <div className="bg-[#10B981] bg-opacity-10 p-6 rounded-md border-l-4 border-[#10B981]">
                <p className="text-base leading-relaxed font-medium text-[#0A0A0A]" data-testid="about-mission">{about.mission}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 bg-[#F4F5F7]" data-testid="contact-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl sm:text-4xl tracking-tight leading-tight font-bold text-[#0A0A0A] mb-4" data-testid="contact-title">{contact.title}</h2>
            <p className="text-base leading-relaxed font-normal text-[#52525B]" data-testid="contact-subtitle">{contact.subtitle}</p>
          </motion.div>

          <motion.form
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.6 }}
            onSubmit={handleSubmit}
            className="bg-white p-8 rounded-md border border-[#E4E4E7] space-y-6"
            data-testid="contact-form"
          >
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-[#0A0A0A] mb-2">Name</label>
              <Input
                id="name"
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full focus:ring-2 focus:ring-[#10B981]"
                data-testid="contact-form-name"
              />
            </div>
            <div>
              <label htmlFor="organization" className="block text-sm font-semibold text-[#0A0A0A] mb-2">Organization</label>
              <Input
                id="organization"
                type="text"
                required
                value={formData.organization}
                onChange={(e) => setFormData({ ...formData, organization: e.target.value })}
                className="w-full focus:ring-2 focus:ring-[#10B981]"
                data-testid="contact-form-organization"
              />
            </div>
            <div>
              <label htmlFor="jurisdiction" className="block text-sm font-semibold text-[#0A0A0A] mb-2">Jurisdiction of Interest</label>
              <Input
                id="jurisdiction"
                type="text"
                required
                value={formData.jurisdiction}
                onChange={(e) => setFormData({ ...formData, jurisdiction: e.target.value })}
                className="w-full focus:ring-2 focus:ring-[#10B981]"
                data-testid="contact-form-jurisdiction"
              />
            </div>
            <div>
              <label htmlFor="message" className="block text-sm font-semibold text-[#0A0A0A] mb-2">Message</label>
              <Textarea
                id="message"
                required
                rows={5}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="w-full focus:ring-2 focus:ring-[#10B981]"
                data-testid="contact-form-message"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#10B981] hover:bg-[#059669] text-white rounded-md py-6 text-base font-semibold transition-all"
              data-testid="contact-form-submit"
            >
              {submitting ? 'Submitting...' : 'Send Message'}
            </Button>
          </motion.form>
        </div>
      </section>

      <Footer />
    </div>
  );
};
